















































































































































































import { defineComponent, ref, computed, watch, Ref } from '@vue/composition-api';
import store from '@/store';
import { useVideos, useClient } from '@/use';

import { EvaluateVideoPayload, VideoList, Models, UserLevel } from '@/types';
import { sleep } from '@/utils';
import { initiateAnalysisOpen, startLatestTaskPolling } from '../state';
import { snackbar } from '@/components/GlobalSnackbar';

const headers = [
  { text: 'Video Filename', value: 'name' },
  { text: 'Subject Type', value: 'subject_type', sortable: false },
  { text: 'Subject Sex', value: 'subject_sex', sortable: false },
  { text: 'Data Source', value: 'data_source', sortable: false },
  { text: 'Details', value: 'details', sortable: false },
  { text: 'Total Frames', value: 'number_of_frames' },
];

type EvaluateMode = 'low' | 'med' | 'high' | 'very_high';
const sampleRateItems = [
  { text: 'Low (0.01 frames/second, 1 frame every 100 seconds) ', value: 'low' },
  { text: 'Medium (0.1 frames/second, 1 frame every 10 seconds) ', value: 'med' },
  { text: 'High (1 frame/second) ', value: 'high' },
  { text: 'Very High (1 frame/ 10 frames) ', value: 'very_high' },
] as {text: string; value: EvaluateMode}[];

const motionModels = [
  { text: 'Transformer', value: 'yulu' },
  { text: 'Event Camera', value: 'andrew' },
];
const isScorer = computed(() => store.getters.isScorer);
const isScorerOrAnnotator = computed(() => store.getters.isScorerOrAnnotator);
const isAdmin = computed(() => store.getters.isAdmin);
const evaluationIntervalMin = computed(() => (isScorerOrAnnotator.value ? 30 : 300));
const evaluationIntervalMax = ref(1000);

export default defineComponent({
  name: 'InitiateAnalysisMenu',
  setup() {
    const client = useClient();
    const videoSubjectTypes = computed(() => store.state.application.applicationData?.lists.subject_types.map((st) => st.toUpperCase()));
    const subjectSex = computed(() => store.state.application.applicationData?.lists.subject_sex);
    const dataSources = computed(() => {
      // If user level is standard user, just return their allowed institutions
      if (client?.user?.user_level === UserLevel.User || client?.user?.user_level === UserLevel.Userhigh) {
        return client.user?.accessible_institutions;
      }

      // Otherwise return all possible
      return store.state.application.applicationData?.lists.institutions;
    });
    const {
      loading,
      items,
      selected,
      fetch,
      videoTableInfo,
      videoTableOptions,
      itemsPerPage,
      search,
      filterSubjectType,
      filterSubjectSex,
      filterDataSource,
    } = useVideos();

    function closeDialog() {
      initiateAnalysisOpen.value = false;
    }

    function selectVideo(video: VideoList) {
      selected.value = [video];
    }

    const modelOptions: Ref<Models> = ref({});
    const selectedOptions = ref({
      fau: [''],
      'pain-mgs': [''],
    });
    const subjectTypes = ref<string[]>([]);
    const selectedSubject = ref('general');
    const selectedFPModels = ref({
      fau: 'default',
      'pain-mgs': 'default',
    });
    const selectedMotionModel = ref(motionModels[0].value);

    async function getModelOptions() {
      const modelsResponse = await client.getModels();
      subjectTypes.value = Object.keys(modelsResponse);
      modelOptions.value = modelsResponse;

      // eslint-disable-next-line dot-notation
      selectedOptions.value = modelOptions.value['general'];
    }

    function updateSelectedModels() {
      [selectedFPModels.value.fau] = selectedOptions.value.fau;
      selectedOptions.value.fau.forEach((element) => { if (element.match(/default\(([^)]+)\)/)) { selectedFPModels.value.fau = element; } });
      // selectedFPModels.value.fau = selectedOptions.value.fau.includes('default') ? 'default' : selectedOptions.value.fau[0];
      [selectedFPModels.value['pain-mgs']] = selectedOptions.value['pain-mgs'];
      selectedOptions.value['pain-mgs'].forEach((element) => { if (element.match(/default\(([^)]+)\)/)) { selectedFPModels.value['pain-mgs'] = element; } });
      // selectedFPModels.value['pain-mgs'] = selectedOptions.value['pain-mgs'].includes('default') ? 'default' : selectedOptions.value['pain-mgs'][0];
    }

    function updateOptions() {
      selectedOptions.value = modelOptions.value[selectedSubject.value];
      updateSelectedModels();
    }

    // Update the selected subject type and model options based on the selected videos
    const defaultSubjectType = 'general';
    watch(selected, () => {
      if (selected.value.length === 0) {
        selectedSubject.value = defaultSubjectType;
        return;
      }
      const referenceSubjectType = selected.value[0].subject_type.replace(/[^a-zA-Z0-9 ]/g, '').toLowerCase();

      // If all subject types aren't the same or don't exist in 'subjectTypes' list, set subject type to general
      const allMatch = selected.value.every((video) => video.subject_type === selected.value[0].subject_type);
      const subjectTypeAllowed = allMatch && subjectTypes.value.includes(referenceSubjectType);
      selectedSubject.value = subjectTypeAllowed ? referenceSubjectType : defaultSubjectType;
    });

    // Watch subject change from video selection or direct change
    watch(selectedSubject, updateOptions);

    // Evaluate Form
    const overwrite = true; // Pin this for now
    const evaluateFormValid = ref(false);
    const evaluateMotion = ref(false);
    const evaluateMode = ref<EvaluateMode>('low');
    const evaluateRequestLoading = ref(false);
    const filteredSampleRateItems = computed(() => {
      const filtered = [...sampleRateItems];
      // if (!store.getters.isScorer && client?.user?.user_level !== UserLevel.Userhigh) {
      //   // Remove high evaluation mode
      //   filtered.splice(filtered.findIndex((it) => it.value === 'high'), 1);
      // }

      return filtered;
    });
    const evaluateIntervals = computed(() => (
      selected.value.map((video) => {
        let mult = 100;
        switch (evaluateMode.value) {
          case 'med':
            mult = 10;
            break;
          case 'high':
            mult = 1;
            break;
          case 'very_high':
            return 10; // pass interval as 10
          default:
            break;
        }

        return Math.round(Math.max(video.frame_per_second, 30)) * mult;
      })
    ));

    const evaluationSliderHint = `
      1 frame evaluated every X video frames.
      X can be set between ${evaluationIntervalMin.value}-${evaluationIntervalMax.value}.
    `;

    async function evaluateSelectedVideos() {
      if (!selected.value.length) {
        throw new Error('Need a client instance and selected videoS to evaluate!');
      }

      let latestTaskId = '';
      let dirty = false;
      evaluateRequestLoading.value = true;

      // eslint-disable-next-line no-restricted-syntax
      for (const [index, video] of selected.value.entries()) {
        // Use evaluateMotion method if selected
        const method = (evaluateMotion.value ? client.evaluateMotion : client.evaluateVideo).bind(client);
        const payload: EvaluateVideoPayload = {
          video_filename: video.name,
          fau_model_id: `${selectedSubject.value}/fau/${selectedFPModels.value.fau.split('(')[0]}`, // ex. default from default(generalv5)
          mgs_model_id: `${selectedSubject.value}/pain-mgs/${selectedFPModels.value['pain-mgs'].split('(')[0]}`,
          motion_model_id: selectedMotionModel.value,
          interval: evaluateIntervals.value[index],
          overwrite,
        };

        // eslint-disable-next-line no-await-in-loop
        const check = await client.evaluationPrecheck(payload);
        if (!check.check) {
          dirty = true;
          snackbar.dispatch({ text: check.reason, options: { color: 'error', top: true } });
          break;
        }

        // Successsful check, call actual method
        // eslint-disable-next-line no-await-in-loop
        latestTaskId = await method(payload);
      }

      // Wait 1 second so when tasks are fetched afterwards,
      // the API correctly responds
      await sleep(1000);

      // Set as last added so tasks can update
      store.commit.application.setLatestAddedTaskId(latestTaskId);
      startLatestTaskPolling();

      evaluateRequestLoading.value = false;
      if (!dirty) {
        closeDialog();
      }
    }

    if (!isAdmin.value) {
      sampleRateItems.pop(); // if not admin, remove very_high interval
    }
    watch(initiateAnalysisOpen, (open) => {
      if (open) {
        fetch();
        getModelOptions();
      }
    });

    return {
      filteredSampleRateItems,
      headers,
      search,
      filterSubjectType,
      filterSubjectSex,
      filterDataSource,
      items,
      loading,
      selected,
      closeDialog,
      selectVideo,
      videoTableOptions,
      videoTableInfo,
      itemsPerPage,
      subjectTypes,
      subjectSex,
      videoSubjectTypes,
      dataSources,
      updateOptions,
      selectedSubject,
      selectedOptions,
      selectedFPModels,

      // Evaluate Form
      evaluateRequestLoading,
      evaluateFormValid,
      evaluationIntervalMin,
      evaluationIntervalMax,
      evaluateMode,
      evaluationSliderHint,
      evaluateSelectedVideos,
      isScorer,
      evaluateMotion,

      // Motion selection
      selectedMotionModel,
      motionModels,
    };
  },
});
